/**
 * The API's answer when Cal is throttling CMS (AB#514): 503 with a body of
 * `{ error, code: "calThrottled", retryAfterSeconds }` and a `Retry-After` header — see
 * CalThrottledExceptionHandler on the server.
 *
 * Both API slices (staff `services/api.ts`, public `services/publicApi.ts`) are RTK Query over
 * fetchBaseQuery, so the error is a FetchBaseQueryError `{ status, data }` whether it arrives as a
 * hook's `error`, a mutation's `result.error`, or is thrown by `.unwrap()`. That one shape is all
 * this reads.
 */

/** The `code` in the 503 body. */
export const CAL_THROTTLED_CODE = "calThrottled"

/** How long to wait when the body does not say. */
export const DEFAULT_RETRY_AFTER_SECONDS = 60

/** The shortest and longest waits the page will count down. */
export const MIN_RETRY_AFTER_SECONDS = 1
export const MAX_RETRY_AFTER_SECONDS = 600

function bodyOf(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== "object") return undefined
  const data = (error as { data?: unknown }).data
  return data && typeof data === "object" && !Array.isArray(data) ? data as Record<string, unknown> : undefined
}

/**
 * Whether `error` is the throttled 503. Matched on status and code together: a 503 for any other
 * reason is a generic failure, and the code on anything but a 503 is not this answer.
 */
export function isCalThrottled(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  if ((error as { status?: unknown }).status !== 503) return false
  return bodyOf(error)?.code === CAL_THROTTLED_CODE
}

/**
 * How many seconds the server asked the caller to wait, from the body's `retryAfterSeconds`:
 * rounded up to a whole second and clamped to 1–600. 60 when the body carries no usable number.
 */
export function retryAfterSeconds(error: unknown): number {
  const value = bodyOf(error)?.retryAfterSeconds
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_RETRY_AFTER_SECONDS
  return Math.min(MAX_RETRY_AFTER_SECONDS, Math.max(MIN_RETRY_AFTER_SECONDS, Math.ceil(value)))
}

/** The first of `errors` that is the throttled 503, or null. For views made of several reads. */
export function firstCalThrottled(...errors: unknown[]): unknown {
  return errors.find(isCalThrottled) ?? null
}
